// CTW-DURMAH-STUDENT-MANUAL-01:
// Student Manual section page.
//
// Statically generated from the manual index — the content is compiled into
// the bundle, so there is no data fetch and no loading state.
//
// GATING: member-only sections render a teaser for signed-out visitors. The
// body is withheld at render time via visibleBlocks(), which is the single
// enforcement point. The section's title and summary remain public and
// indexable by design — a locked door should say what is behind it.

import React, { useMemo } from "react";
import Head from "next/head";
import Link from "next/link";
import type { GetStaticPaths, GetStaticProps } from "next";
import { ArrowLeft, ArrowRight, Clock, Lock } from "lucide-react";
import {
  getAdjacentSections,
  getAllManualSlugs,
  getManualCategory,
  getManualSection,
  getRelatedSections,
  isGated,
  visibleBlocks,
  type ManualSection,
} from "@/content/manual";
import ManualSearch from "@/components/manual/ManualSearch";
import {
  ManualBlocks,
  ManualBreadcrumbs,
  ManualDisclaimer,
  ManualHelpFooter,
  ManualMemberGate,
} from "@/components/manual/ManualPrimitives";
import { useAuth } from "@/lib/supabase/AuthContext";

const SITE = "https://www.durmah.ai";

interface ManualSectionPageProps {
  slug: string;
}

export default function ManualSectionPage({ slug }: ManualSectionPageProps) {
  const { user } = useAuth();
  const isSignedIn = Boolean(user);

  const section = useMemo(() => getManualSection(slug), [slug]);
  const category = section ? getManualCategory(section.category) : null;
  const related = useMemo(
    () => (section ? getRelatedSections(section) : []),
    [section],
  );
  const { previous, next } = useMemo(
    () => getAdjacentSections(slug),
    [slug],
  );

  if (!section) {
    return (
      <div className="bg-white dark:bg-[#020617] min-h-screen pt-32">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            That manual section does not exist
          </h1>
          <Link
            href="/manual"
            prefetch={false}
            className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline"
          >
            Back to the Student Manual
          </Link>
        </div>
      </div>
    );
  }

  const gated = isGated(section, isSignedIn);
  const blocks = visibleBlocks(section, isSignedIn);
  const canonical = `${SITE}/manual/${section.slug}`;
  const title = `${section.title} | Durmah Student Manual`;

  const headings = blocks.filter(
    (b): b is Extract<ManualSection["blocks"][number], { kind: "heading" }> =>
      b.kind === "heading",
  );

  const articleSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: section.title,
    description: section.summary,
    url: canonical,
    isAccessibleForFree: section.audience === "public",
    author: { "@type": "Organization", name: "Durmah" },
    publisher: {
      "@type": "Organization",
      name: "Durmah",
      logo: {
        "@type": "ImageObject",
        url: `${SITE}/android-chrome-512x512.png`,
      },
    },
  });

  const breadcrumbSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Student Manual",
        item: `${SITE}/manual`,
      },
      { "@type": "ListItem", position: 2, name: section.title, item: canonical },
    ],
  });

  return (
    <div className="bg-white dark:bg-[#020617] min-h-screen flex flex-col pt-32">
      <Head>
        <title>{title}</title>
        <meta name="description" content={section.summary} />
        <link key="route-canonical" rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={section.summary} />
        <meta key="route-og-url" property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={section.summary} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: articleSchema }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: breadcrumbSchema }}
        />
      </Head>

      <main className="flex-1 py-10 lg:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search stays available on every section page */}
          <div className="mb-8">
            <ManualSearch isSignedIn={isSignedIn} />
          </div>

          <ManualBreadcrumbs
            category={category ? { label: category.label, id: category.id } : null}
            current={section.title}
          />

          {/* Header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {section.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] font-black uppercase tracking-tighter px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400"
                >
                  {tag}
                </span>
              ))}
              {section.audience === "member" ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  <Lock className="w-2.5 h-2.5" aria-hidden="true" />
                  Members
                </span>
              ) : null}
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 ml-auto">
                <Clock className="w-3 h-3" aria-hidden="true" />
                {section.readTime}
              </span>
            </div>

            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 dark:text-white leading-tight mb-4">
              {section.title}
            </h1>

            <p className="text-lg leading-relaxed text-gray-600 dark:text-slate-400">
              {section.summary}
            </p>
          </header>

          {/* On this page */}
          {headings.length > 1 ? (
            <nav
              aria-label="On this page"
              className="mb-10 rounded-2xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/40 p-5"
            >
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3">
                On this page
              </p>
              <ul className="space-y-1.5">
                {headings.map((h) => (
                  <li key={h.id}>
                    <a
                      href={`#${h.id}`}
                      className="text-sm font-medium text-gray-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    >
                      {h.text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}

          {/* Body, or the gate */}
          {gated ? (
            <ManualMemberGate section={section} />
          ) : (
            <ManualBlocks blocks={blocks} />
          )}

          {/* Related */}
          {related.length > 0 ? (
            <section className="mt-14">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                Keep going
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    href={`/manual/${r.slug}`}
                    prefetch={false}
                    className="group rounded-2xl border border-gray-200 dark:border-slate-800 p-5 hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-colors"
                  >
                    <p className="text-sm font-bold text-gray-900 dark:text-white mb-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                      {r.title}
                    </p>
                    <p className="text-xs leading-relaxed text-gray-500 dark:text-slate-400 line-clamp-2">
                      {r.summary}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          {/* Previous / next */}
          <nav
            aria-label="Manual navigation"
            className="mt-12 flex flex-col sm:flex-row gap-4 justify-between"
          >
            {previous ? (
              <Link
                href={`/manual/${previous.slug}`}
                prefetch={false}
                className="group flex-1 flex items-center gap-3 rounded-2xl border border-gray-200 dark:border-slate-800 p-4 hover:border-indigo-300 transition-colors"
              >
                <ArrowLeft
                  className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 transition-colors flex-shrink-0"
                  aria-hidden="true"
                />
                <span>
                  <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Previous
                  </span>
                  <span className="block text-sm font-bold text-gray-900 dark:text-white">
                    {previous.title}
                  </span>
                </span>
              </Link>
            ) : (
              <span className="flex-1" />
            )}

            {next ? (
              <Link
                href={`/manual/${next.slug}`}
                prefetch={false}
                className="group flex-1 flex items-center justify-end gap-3 rounded-2xl border border-gray-200 dark:border-slate-800 p-4 hover:border-indigo-300 transition-colors text-right"
              >
                <span>
                  <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Next
                  </span>
                  <span className="block text-sm font-bold text-gray-900 dark:text-white">
                    {next.title}
                  </span>
                </span>
                <ArrowRight
                  className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 transition-colors flex-shrink-0"
                  aria-hidden="true"
                />
              </Link>
            ) : (
              <span className="flex-1" />
            )}
          </nav>

          <ManualHelpFooter />
          <ManualDisclaimer />
        </div>
      </main>
    </div>
  );
}

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: getAllManualSlugs().map((slug) => ({ params: { slug } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<ManualSectionPageProps> = async (
  ctx,
) => {
  const slug = String(ctx.params?.slug ?? "");
  if (!getManualSection(slug)) return { notFound: true };
  return { props: { slug } };
};
